import React, { useState, useEffect } from "react";
import { Formik } from "formik";
import * as yup from "yup";
import Modal from "../../../../components/modal";
import Select from "../../../../components/form/select";
import Dropzone from "react-dropzone";
import Button from "../../../../components/new-button";
import styles from "./styles.module.scss";
import useTicketStore from "../../../../stores/ticket";
import { fetchTicketOptions, addComment, fetchTicketDetails, fetchTickets } from "../../../../services/ticket";
import { FileIconColorMapping } from "../../../../pages/create-app/components/application-form/components/file-upload/utils";
import UploadIcon from "../../../../assets/svg/icons/common/upload";
import Trash from "../../../../assets/svg/icons/common/Trash";


const schema = yup.object({
  comment: yup.string().required("* Comment is required").max(5000, "* Comment must be 5000 characters or fewer"),
  status: yup.string().oneOf(["", "O", "C", "H"]).nullable(),
});

export const AddCommentModal = ({ isOpen, onClose, ticketId, canUpdateStatus = false }) => {
  const [submitError, setSubmitError] = useState(null);
  const [files, setFiles] = useState([]);
  const options = useTicketStore((state) => state.options);

  const handleFileUpload = (acceptedFiles) => {
    setFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
  };

  const handleFileRemove = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  useEffect(() => {
    if (isOpen && ticketId != null) {
      fetchTicketOptions();
    }
  }, [isOpen, ticketId]);


  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    try {
      setSubmitError(null);
      if (ticketId == null) {
        setSubmitError('Missing ticket');
        setSubmitting(false);
        return;
      }
      const formData = new FormData();

      formData.append('id', ticketId);
      if (canUpdateStatus) {
        formData.append('status_id', values.status ?? "");
      }

      formData.append('comment', values.comment || "");

      if (files.length > 0) {
        files.forEach(file => {
          formData.append('comment_attachment[]', file);
        });
      }

      const response = await addComment(formData);

      if (response.status) {
        await Promise.all([
          fetchTicketDetails(ticketId),
          canUpdateStatus && values.status ? fetchTickets() : Promise.resolve()
        ]);
        setFiles([]);
        onClose();
      } else {
        setSubmitError(response.message || 'Failed to add comment');
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      // The shared axios instance rejects with error.response itself (api.js:56),
      // so the payload is at error.data — error.response.data is never set here,
      // which is why permission and validation messages used to fall through to
      // the generic text. A transport failure rejects with a bare string
      // (api.js:62).
      if (error?.data?.errors) {
        setErrors(error.data.errors);
      } else {
        setSubmitError(
          error?.data?.message ||
          (typeof error === "string" ? error : null) ||
          'Failed to add comment'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header="Add comment"
      size="sm"
    >
      <Formik
        initialValues={{
          owner_group: "",
          problem_type: "",
          comment: "",
          status: "",
        }}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit} className={styles.form}>

            {canUpdateStatus && (
            <div className={styles.formGroup}>
              <Select
                label="Status"
                name="status"
                value={values.status}
                onChange={(value) => {
                  handleChange({
                    target: { name: "status", value }
                  });
                }}
                onBlur={handleBlur}
                error={touched.status && errors.status}
                placeholder="Update status"
                options={options.statuses}
                loading={false}
              />
            </div>
            )}

            <div className={styles.formGroup} style={{ gap: 0 }}>
              <label>Comment</label>
              <textarea
                name="comment"
                value={values.comment}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Add comment"
                maxLength={5000}
              />
              {touched.comment && errors.comment && (
                <div className={styles.error}>{errors.comment}</div>
              )}
            </div>

            <div className={styles.fileUpload}>
              <Dropzone
                onDrop={handleFileUpload}
                minSize={100}
                maxSize={10 * 1024 * 1024}
                accept={{
                  "image/jpeg": [".jpg", ".jpeg"],
                  "image/png": [".png"],
                  "image/gif": [".gif"],
                  "application/pdf": [".pdf"],
                  "application/msword": [".doc"],
                  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
                  "application/vnd.ms-excel": [".xls"],
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                }}
                noClick={false}
                noKeyboard={false}
              >
                {({ getRootProps, getInputProps }) => (
                  <section className={styles.dnd}>
                    <div {...getRootProps()} className={styles.dnd_section}>
                      <input {...getInputProps()} />
                      <div className={styles.inner_content}>
                        <UploadIcon />
                        <div>
                          <p>
                            Upload documents by drag and drop or{" "}
                            <span>click to upload</span>
                          </p>
                          <p>jpeg, jpg, png, gif, pdf, doc, docx, xls, xlsx</p>
                        </div>
                      </div>
                    </div>
                  </section>
                )}
              </Dropzone>

              {files.length > 0 && (
                <div className={styles.fileList}>
                  {files.map((file, index) => (
                    <div key={index} className={styles.fileItem}>
                      <div 
                        className={styles.fileIcon}
                        style={{
                          backgroundColor: FileIconColorMapping[file.name.split('.').pop().toLowerCase()] || '#D99F00'
                        }}
                      >
                        {file.name.split('.').pop().slice(0, 3).toUpperCase()}
                      </div>
                      <div className={styles.fileInfo} title={file.name}>
                        <span className={styles.fileName}>{file.name}</span>
                        <span className={styles.fileSize}>
                          {Math.round(file.size / 1024)}kb
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleFileRemove(index)}
                        className={styles.removeFile}
                      >
                        <Trash color="#343330" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {submitError && (
              <div className={styles.errorMessage}>
                {submitError}
              </div>
            )}

            <div className={styles.actions}>
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
                loading={isSubmitting}
                loadingText="Sending..."
                onClick={handleSubmit}
              >
                {isSubmitting ? 'Sending...' : 'Send'}
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
};
